import { Component, OnInit, Renderer2 } from '@angular/core';
import { FuseAlertType } from '@fuse/components/alert';
import { SnackbarService } from 'app/core/mips/common/services/snackbar.service';
import { UserModel } from 'app/core/mips/common/models/user.model';
import { ConsultantModel } from 'app/core/mips/common/models/consultant.model';
import { URLPaths } from 'app/core/mips/common/utilities/urlPaths.config';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { setLoggedInUser } from 'app/core/store/loggedInUser/loggedInUser.action';
import { setUser } from 'app/core/store/currentUser/user.actions';
import { LocalStorageService } from 'app/core/mips/common/services/local-storage.service';
import { Constants } from 'app/core/mips/common/utilities/constants.config';
import { MsalAuthService } from '../msal-auth.service';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent implements OnInit {
    alert: { type: FuseAlertType; message: string } = {
        type   : 'success',
        message: ''
    };
    showAlert: boolean = false;
    redirectUrl: string = '';
    msalLoginInProgress: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _snackbarService: SnackbarService,
        private _router: Router,
        private _store: Store,
        private _renderer: Renderer2,
        private _msalAuthService: MsalAuthService // removed unused MsalService injection — setActiveAccount now lives inside msal-auth.service.ts
    ) {
        const element = document.querySelector(".mat-mdc-form-field.mat-form-field-appearance-fill .mat-mdc-form-field-subscript-wrapper");
        if(element){
          this._renderer.setStyle(element, 'display', 'none', 1);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Only auto-authenticate on login page if returning from an active MSAL redirect callback
        if (this.isMsalRedirectReturn() && this._msalAuthService.isLoggedIn() && !this.isPopupOrIframe()) {
            this.handleMsalAuthentication();
        }
    }

    setCurrentUser(user: ConsultantModel) {
        this._store.dispatch(setUser({ user }));
    }

    setLoggedInUser(user: UserModel) {
        this._store.dispatch(setLoggedInUser({ user }));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in with Microsoft
     */
    signInWithMicrosoft(): void {

      if (this.msalLoginInProgress) {
        return;
      }

      this.msalLoginInProgress = true;
      sessionStorage.setItem('MSAL_REDIRECT_IN_PROGRESS', 'true');
      this._snackbarService.showSnackBar('Authenticating with Microsoft...', '', 'info', true);

      this._msalAuthService.login(true).subscribe({
        next: () => {
          // MSAL loginRedirect initiates page navigation to Microsoft.
          // Sync with backend occurs in handleMsalAuthentication() when Microsoft redirects back.
        },
        error: (error) => {
          console.error('Login redirect failed:', error);
          this.msalLoginInProgress = false;
          sessionStorage.removeItem('MSAL_REDIRECT_IN_PROGRESS');
          this._msalAuthService.clearAuthenticationState();
          this._snackbarService.showSnackBar('Authentication failed', 'close', 'failure');
        }
      });
    }

    /**
     * Handle MSAL authentication success
     */
    private handleMsalAuthentication(): void {
      if (this.msalLoginInProgress) {
        return;
      }
      this.msalLoginInProgress = true;
      this._snackbarService.showSnackBar(Constants.SNACKBAR_MSG_LOGIN_RETRIEVING_DETAILS, '', 'info', true);

      this._msalAuthService.authenticateAndSync().subscribe({
        next: (userData) => {
          sessionStorage.removeItem('MSAL_REDIRECT_IN_PROGRESS');
          if (userData) {
            // Set logged in user in store
            this.setLoggedInUser(userData.user_details);

            // Set consultant details in store
            this.setCurrentUser(userData.consultant_details);

            // Navigate to destination
            const targetUrl = this.redirectUrl || URLPaths.ROUTE_TIME_ENTRY_ACTUALS;

            this._router.navigate([targetUrl], { replaceUrl: true }).then(() => {
              this._snackbarService.dismissSnackBar();
              this.msalLoginInProgress = false;
            }).catch(err => {
              console.error('Navigation failed:', err);
              this.msalLoginInProgress = false;
            });
          } else {
            console.error('handleMsalAuthentication: authenticateAndSync returned null');
            this._snackbarService.showSnackBar('Failed to retrieve user details', 'close', 'failure');
            this.msalLoginInProgress = false;
          }
        },
        error: (error) => {
          sessionStorage.removeItem('MSAL_REDIRECT_IN_PROGRESS');
          this._msalAuthService.clearAuthenticationState();
          this._snackbarService.showSnackBar(error.error?.detail || 'Authentication failed', 'close', 'failure');
          this.msalLoginInProgress = false;
        }
      });
    }

    private isMsalRedirectReturn(): boolean {
      const url = window.location.href;
      const hasMsalHash = url.includes('code=') || url.includes('id_token=') || url.includes('state=') || url.includes('client_info=') || url.includes('error=');
      const isRedirectPending = sessionStorage.getItem('MSAL_REDIRECT_IN_PROGRESS') === 'true';
      return hasMsalHash || isRedirectPending;
    }

    private isPopupOrIframe(): boolean {
      const isPopupName = !!(window.name && window.name.indexOf('msal') > -1);
      const isPopupOpener = !!(window.opener && window.opener !== window);
      const isIframe = window.self !== window.top;

      return isPopupName || isPopupOpener || isIframe;
    }
}